import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  Check,
  CreditCard,
  Crown,
  ExternalLink,
  Loader2,
  RefreshCw,
  Sparkles,
  XCircle,
  Ban,
  Undo2,
} from "lucide-react";
import { useSubscription, type PlanName } from "@/hooks/use-subscription";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "@/hooks/use-toast";

const PLAN_LABEL: Record<PlanName, string> = {
  free: "Plano Básico",
  pro: "Plano Pro",
  premium: "Plano Premium",
};

const STATUS_LABEL: Record<string, { label: string; tone: "ok" | "warn" | "err" }> = {
  active: { label: "Ativa", tone: "ok" },
  trialing: { label: "Em período de teste", tone: "ok" },
  past_due: { label: "Pagamento pendente", tone: "warn" },
  unpaid: { label: "Pagamento não confirmado", tone: "err" },
  canceled: { label: "Cancelada", tone: "err" },
  incomplete: { label: "Pagamento incompleto", tone: "warn" },
  incomplete_expired: { label: "Sessão expirada", tone: "err" },
  paused: { label: "Pausada", tone: "warn" },
};

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatPrice(brl: number | null | undefined) {
  if (brl == null) return "—";
  return brl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function MinhaAssinatura() {
  const { plan, subscription, limits, loading, refresh } = useSubscription();
  const { usage, limits: planLimits, refreshUsage } = usePlanLimits();
  const [opening, setOpening] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const refreshAll = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      await Promise.all([refresh(), refreshUsage()]);
      setLastSyncedAt(new Date());
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  // Sincroniza ao abrir a tela (e quando a aba volta a ficar visível)
  useEffect(() => {
    refreshAll(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAll(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenPortal = async () => {
    try {
      setOpening(true);
      // Garante que estamos olhando para o estado mais atual antes de
      // pedir a sessão do portal (evita abrir o portal de uma assinatura
      // que já foi cancelada/atualizada em outra aba).
      await refreshAll(true);
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: {
          returnUrl: `${window.location.origin}/app/assinatura`,
          environment: getStripeEnvironment(),
        },
      });
      if (error || !data?.url) {
        throw new Error(error?.message || data?.error || "Não foi possível abrir o portal");
      }
      window.open(data.url as string, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast({
        title: "Erro ao abrir portal",
        description: err instanceof Error ? err.message : "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setOpening(false);
    }
  };

  const handleToggleCancel = async (reactivate: boolean) => {
    try {
      setCanceling(true);
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: {
          environment: getStripeEnvironment(),
          reactivate,
        },
      });
      if (error || !data?.ok) {
        throw new Error(error?.message || data?.error || "Não foi possível processar.");
      }
      toast({
        title: reactivate ? "Assinatura reativada" : "Cancelamento agendado",
        description: reactivate
          ? "Sua assinatura continuará renovando normalmente."
          : "Você terá acesso até o fim do período já pago.",
      });
      await refreshAll(true);
    } catch (err) {
      toast({
        title: "Erro",
        description: err instanceof Error ? err.message : "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 px-4 py-8 sm:px-6 lg:px-10">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full max-w-3xl" />
        <Skeleton className="h-32 w-full max-w-3xl" />
      </div>
    );
  }

  const isPaid = plan !== "free";
  const status = subscription?.status ?? (isPaid ? "active" : "free");
  const statusInfo = STATUS_LABEL[status];
  const cancelScheduled = subscription?.cancel_at_period_end === true;
  const trialEnd = subscription?.trial_end ? new Date(subscription.trial_end) : null;
  const isTrialing = trialEnd && trialEnd > new Date();
  const PlanIcon = plan === "premium" ? Crown : Sparkles;
  const isCourtesy =
    subscription?.stripe_subscription_id?.startsWith("comp-") ||
    subscription?.stripe_subscription_id?.startsWith("manual_") ||
    subscription?.stripe_customer_id === "comp" ||
    subscription?.stripe_customer_id?.startsWith("manual_");

  return (
    <div className="space-y-6 px-4 py-8 sm:px-6 lg:px-10">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Conta · Assinatura
          </p>
          <h1 className="font-serif text-3xl font-light text-foreground sm:text-4xl">
            Minha assinatura
          </h1>
          <p className="text-sm text-muted-foreground">
            Acompanhe seu plano, próximos pagamentos e gerencie a cobrança.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastSyncedAt && (
            <span className="text-[11px] text-muted-foreground">
              Atualizado às{" "}
              {lastSyncedAt.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshAll(false)}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
            )}
            Atualizar
          </Button>
        </div>
      </header>

      {/* Banners de estado */}
      {status === "past_due" && (
        <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <p className="font-medium text-foreground">Pagamento pendente</p>
            <p className="text-muted-foreground">
              Tivemos um problema com sua última cobrança. Atualize seu método de
              pagamento para evitar a interrupção do serviço.
            </p>
          </div>
        </div>
      )}

      {cancelScheduled && subscription?.current_period_end && (
        <div className="flex items-start gap-3 rounded-md border border-lt-border bg-lt-bg2 px-4 py-3 text-sm">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <p className="font-medium text-foreground">Cancelamento agendado</p>
            <p className="text-muted-foreground">
              Sua assinatura permanece ativa até{" "}
              <span className="text-foreground">{formatDate(subscription.current_period_end)}</span>
              . Após essa data, você voltará para o plano Básico.
            </p>
          </div>
        </div>
      )}

      {/* Card principal de plano */}
      <Card className="max-w-3xl border-lt-border bg-lt-bg2">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <PlanIcon className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <CardTitle className="font-serif text-2xl font-light">
                {PLAN_LABEL[plan]}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {limits?.display_name ?? PLAN_LABEL[plan]}
              </p>
            </div>
          </div>
          {isPaid && statusInfo && (
            <span
              className={
                "rounded-full border px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider " +
                (statusInfo.tone === "ok"
                  ? "border-lt-teal/40 bg-lt-teal/10 text-lt-teal"
                  : statusInfo.tone === "warn"
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-500"
                    : "border-destructive/40 bg-destructive/10 text-destructive")
              }
            >
              {statusInfo.label}
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {isPaid ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Valor
                </p>
                <p className="mt-1 font-serif text-xl text-foreground">
                  {formatPrice(limits?.monthly_price_brl)}
                  <span className="ml-1 text-xs text-muted-foreground">/mês</span>
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {cancelScheduled ? "Acesso até" : "Próxima cobrança"}
                </p>
                <p className="mt-1 text-sm text-foreground">
                  {formatDate(subscription?.current_period_end)}
                </p>
              </div>
              {isTrialing && trialEnd && (
                <div className="sm:col-span-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
                  <strong className="text-primary">Teste grátis</strong> ativo
                  até {formatDate(trialEnd.toISOString())}.
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Você está no plano Básico. Faça upgrade para desbloquear recursos
              avançados como gerador inteligente, alertas e estatísticas.
            </p>
          )}

          <div className="flex flex-col gap-2 border-t border-lt-border pt-4 sm:flex-row sm:flex-wrap">
            {isPaid && !isCourtesy && (
              <Button
                onClick={handleOpenPortal}
                disabled={opening}
                className="font-medium"
              >
                {opening ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-1 h-4 w-4" />
                )}
                {opening ? "Sincronizando…" : "Gerenciar pagamento"}
                {!opening && <ExternalLink className="ml-1 h-3 w-3" />}
              </Button>
            )}
            {isPaid && !isCourtesy && !cancelScheduled && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={canceling}>
                    {canceling ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <Ban className="mr-1 h-4 w-4" />
                    )}
                    Cancelar assinatura
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancelar assinatura?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Você continuará tendo acesso a todos os recursos do plano atual até{" "}
                      <strong>{formatDate(subscription?.current_period_end)}</strong>. Depois
                      dessa data, sua conta volta automaticamente para o plano Básico e nenhuma
                      cobrança futura será feita. Você pode reativar a qualquer momento antes
                      do vencimento.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Voltar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleToggleCancel(false)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Sim, cancelar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {isPaid && !isCourtesy && cancelScheduled && (
              <Button
                variant="outline"
                onClick={() => handleToggleCancel(true)}
                disabled={canceling}
              >
                {canceling ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Undo2 className="mr-1 h-4 w-4" />
                )}
                Reativar assinatura
              </Button>
            )}
            {isPaid && isCourtesy && (
              <div className="flex-1 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
                <strong className="text-primary">Plano cortesia</strong> concedido pela equipe LotoTerminal —
                sem cobrança. Para mudar, fale com o suporte.
              </div>
            )}
            <Button asChild variant={isPaid ? "outline" : "default"}>
              <Link to="/planos">
                {isPaid ? "Mudar de plano" : "Conhecer os planos pagos"}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Uso vs limites */}
      <Card className="max-w-3xl border-lt-border bg-lt-bg2">
        <CardHeader>
          <CardTitle className="font-serif text-lg font-light">Uso atual</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <UsageBar
            label="Jogos salvos"
            current={usage.saved_games}
            limit={planLimits?.saved_games_limit ?? null}
          />
          <UsageBar
            label="Bolões ativos"
            current={usage.active_pools}
            limit={planLimits?.active_pools_limit ?? null}
          />
        </CardContent>
      </Card>

      {/* Recursos do plano */}
      {limits && (
        <Card className="max-w-3xl border-lt-border bg-lt-bg2">
          <CardHeader>
            <CardTitle className="font-serif text-lg font-light">
              O que está incluído
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-2 text-sm sm:grid-cols-2">
              <FeatureLine ok label="Conferência automática" />
              <FeatureLine
                ok={limits.email_alerts}
                label="Alertas por e-mail"
              />
              <FeatureLine
                ok={limits.closure_generator}
                label="Gerador de fechamento estratégico"
              />
              <FeatureLine
                ok={limits.advanced_stats}
                label="Estatísticas avançadas"
              />
              <FeatureLine ok={limits.exports} label="Exportação PDF/CSV" />
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function UsageBar({
  label,
  current,
  limit,
}: {
  label: string;
  current: number;
  limit: number | null;
}) {
  const unlimited = limit === null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((current / Math.max(1, limit)) * 100));
  const danger = !unlimited && current >= (limit ?? 0);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-[13px]">
        <span className="text-foreground">{label}</span>
        <span className={danger ? "text-destructive" : "text-muted-foreground"}>
          {current} {unlimited ? <em className="not-italic">/ ilimitado</em> : `/ ${limit}`}
        </span>
      </div>
      {!unlimited && <Progress value={pct} className="h-1.5" />}
    </div>
  );
}

function FeatureLine({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-lt-teal" />
      ) : (
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
      )}
      <span className={ok ? "text-foreground" : "text-muted-foreground line-through"}>
        {label}
      </span>
    </li>
  );
}
